import { useEffect, useRef, useState } from "react";
import AudioVisualizer from "../../../../components/AudioVisualizer/AudioVisualizer";
import Image from "../../../../components/images/Image";
import { formatTime } from "../../../../utils/audioUtils";
import config from "../../../../config/config";

// Global ref to keep track of currently playing audio
let currentAudio: HTMLAudioElement | null = null;

const AudioMessage = ({ audioKey,index = 0 }: { audioKey: string,index?:number }) => {
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [timer, setTimer] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const fetchAudio = async () => {
            try {
                const audioPath = audioKey || '';
                const path = audioPath.startsWith('public/') ? audioPath : `public/${audioPath}`;
                const baseUrl = config?.cdnURL || config?.baseURL || '';
                const normalizedBaseUrl = baseUrl ? baseUrl.replace(/\/$/, '') + '/' : '';
                const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
                const url = normalizedBaseUrl + normalizedPath;
                const response = await fetch(url);
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                setAudioBlob(blob);
                setAudioUrl(objectUrl);
            } catch (err) {
                console.error("Failed to fetch audio", err);
            }
        };

        fetchAudio();

        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioKey]);

    // Handle duration loading from the main audio element
    useEffect(() => {
        const audio = audioRef.current;

        if (!audio || !audioUrl) return;

        let retryCount = 0;
        const maxRetries = 3;

        const checkDuration = () => {

            if (!isNaN(audio.duration) && audio.duration !== Infinity && audio.duration > 0) {
                setDuration(Math.floor(audio.duration));
                return true;
            }
            return false;
        };

        const handleLoadedMetadata = () => {
            if (!checkDuration() && retryCount < maxRetries) {
                // If duration is still Infinity, try seeking to get actual duration
                setTimeout(() => {
                    audio.currentTime = 0.1; // Seek slightly forward
                    setTimeout(() => {
                        audio.currentTime = 0; // Reset to beginning
                        checkDuration();
                    }, 100);
                }, 100);
                retryCount++;
            }
        };

        const handleCanPlayThrough = () => {
            if (duration === 0) {
                checkDuration();
            }
        };

        const handleDurationChange = () => {
            if (duration === 0) {
                checkDuration();
            }
        };

        const handleTimeUpdate = () => {
            // Sometimes duration becomes available during playback
            if (duration === 0 && audio.duration !== Infinity && !isNaN(audio.duration) && audio.duration > 0) {
                setDuration(Math.floor(audio.duration));
                audio.removeEventListener("timeupdate", handleTimeUpdate);
            }
        };

        const handleError = (e: Event) => {
            console.error("Audio error:", e);
        };

        // Add all event listeners
        audio.addEventListener("loadedmetadata", handleLoadedMetadata);
        audio.addEventListener("canplaythrough", handleCanPlayThrough);
        audio.addEventListener("durationchange", handleDurationChange);
        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("error", handleError);

        // Set the source and load with different preload strategy
        audio.src = audioUrl;
        audio.preload = "auto"; // Change from metadata to auto
        audio.load();

        return () => {
            audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
            audio.removeEventListener("canplaythrough", handleCanPlayThrough);
            audio.removeEventListener("durationchange", handleDurationChange);
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("error", handleError);
        };
    }, [audioUrl, duration]);

    const handlePlayPause = () => {
        const audio = audioRef.current;
        if (!audio || !audioBlob) return;

        // Stop previously playing audio
        if (currentAudio && currentAudio !== audio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            currentAudio.dispatchEvent(new Event("ended")); // for cleanup
        }

        if (audio.paused) {
            audio.play();
            currentAudio = audio;
            setIsPlaying(true);
            intervalRef.current = setInterval(() => {
                setTimer(Math.floor(audio.currentTime));
            }, 1000);
        } else {
            audio.pause();
            setIsPlaying(false);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }

        audio.onended = () => {
            setIsPlaying(false);
            setTimer(0);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    };

    return audioBlob && audioUrl ? (
        <div className="flex justify-between items-center gap-2 bg-background_blue px-4 py-2 rounded-lg w-[340px] h-[52px]" data-testid={'audio-visualizer'}>
            <button onClick={handlePlayPause} className=" h-[52px]">
                {isPlaying ? (
                    <Image src="pause" testId={`img-play-${index}`} className="h-5 w-4" />
                ) : (
                    <Image src="play" className="h-5 w-4" />
                )}
            </button>

            <AudioVisualizer
                blob={audioBlob}
                width={200}
                height={40}
                barWidth={1}
                gap={1}
                barColor={'#000000'}
                barPlayedColor={'#d92316'}
                currentTime={timer}
                duration={duration}
            />

            <span className="text-sm text-gray-600 w-[60px] text-center" data-testid={`timer-${index}`}>
                {formatTime(isPlaying ? timer : (duration || 0))}
            </span>

            <audio ref={audioRef} hidden preload="auto" />
        </div>
    ) : null;
};

export default AudioMessage;